import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, coverUri } from "../../lib/api";
import { Spinner, EmptyState, Button, Field, Sheet } from "../../components/ui";
import { HighlightToggle, FeatureStar, setFeatured, useFeatureToggle } from "../../components/Highlight";
import { Icon } from "../../components/icons";
import { can } from "../../lib/permissions";
import { useAuth } from "../../auth";

function songIdsFrom(sl) {
  if (Array.isArray(sl?.song_ids) && sl.song_ids.length) return sl.song_ids.map(String);
  return (sl?.songs || []).map((s) => String(s.id)).filter(Boolean);
}

function Editor({ existing, allSongs, members, onClose, onSaved }) {
  const [name, setName] = useState(existing?.name || existing?.title || "");
  const [description, setDescription] = useState(existing?.description || "");
  const [ids, setIds] = useState(existing ? songIdsFrom(existing) : []);
  const [singers, setSingers] = useState((existing?.member_ids || []).map(String));
  const [search, setSearch] = useState("");
  const [featured, setFeatured] = useState(!!existing?.featured);
  const [error, setError] = useState("");

  const byId = Object.fromEntries((allSongs || []).map((s) => [String(s.id), s]));
  const inList = new Set(ids);
  const available = (allSongs || []).filter(
    (s) => !inList.has(String(s.id)) && `${s.title} ${s.artist || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const [dragIndex, setDragIndex] = useState(null);
  const moveTo = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...ids];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setIds(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    const next = [...ids];
    [next[i], next[j]] = [next[j], next[i]];
    setIds(next);
  };
  const toggleSinger = (id) => setSingers((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), description, song_ids: ids, member_ids: singers };
      const r = await api(existing?.id ? `/admin/setlists/${existing.id}` : "/admin/setlists", {
        method: existing?.id ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
      const sid = existing?.id || r?.id;
      if (sid && featured !== !!existing?.featured) await setFeatured("setlists", sid, featured);
      return r;
    },
    onSuccess: onSaved,
    onError: (e) => setError(e.message),
  });

  return (
    <Sheet title={existing?.id ? "Editar alinhamento" : "Novo alinhamento"} onClose={onClose} testid="setlist-form" wide>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Field label="NOME" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Concerto de Natal" data-testid="setlist-name" />
        <Field as="textarea" label="DESCRIÇÃO" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notas sobre este espetáculo" />
        <HighlightToggle label="Destacar na página inicial" checked={featured} onChange={setFeatured} testid="setlist-featured" />

        <div className="grid-cards" style={{ gridTemplateColumns: "1fr", gap: 20 }}>
          <div className="editor-two">
            {/* Ordered songs in the setlist */}
            <div>
              <div className="t-section" style={{ marginBottom: 8 }}>Músicas do alinhamento ({ids.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="setlist-songs">
                {ids.length ? (
                  ids.map((sid, i) => {
                    const s = byId[sid] || { title: "Música", id: sid };
                    return (
                      <div
                        key={sid}
                        className="card"
                        draggable
                        onDragStart={() => setDragIndex(i)}
                        onDragOver={(ev) => ev.preventDefault()}
                        onDrop={() => { moveTo(dragIndex, i); setDragIndex(null); }}
                        onDragEnd={() => setDragIndex(null)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, opacity: dragIndex === i ? 0.4 : 1 }}
                        data-testid={`setlist-song-${sid}`}
                      >
                        <span style={{ cursor: "grab", color: "var(--muted)", display: "flex", touchAction: "none" }} title="Arraste para reordenar"><Icon name="list" size={16} /></span>
                        <span style={{ width: 20, textAlign: "center", color: "var(--muted)", fontFamily: "var(--f-medium)" }}>{i + 1}</span>
                        <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 38, height: 38, borderRadius: "var(--r-sm)" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "var(--f-medium)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                          <div className="t-hint">{s.artist}</div>
                        </div>
                        <button className="icon-btn" style={{ width: 34, height: 34 }} onClick={() => move(i, -1)} disabled={i === 0} data-testid={`setlist-up-${sid}`}><Icon name="chevron-back" size={15} style={{ transform: "rotate(90deg)" }} /></button>
                        <button className="icon-btn" style={{ width: 34, height: 34 }} onClick={() => move(i, 1)} disabled={i === ids.length - 1} data-testid={`setlist-down-${sid}`}><Icon name="chevron-down" size={15} /></button>
                        <button className="icon-btn" style={{ width: 34, height: 34 }} onClick={() => setIds(ids.filter((x) => x !== sid))} data-testid={`setlist-remove-${sid}`}><Icon name="close" size={15} color="var(--muted)" /></button>
                      </div>
                    );
                  })
                ) : (
                  <div className="t-hint">Adicione músicas a partir da lista ao lado.</div>
                )}
              </div>
            </div>

            {/* Available songs to add */}
            <div>
              <div className="t-section" style={{ marginBottom: 8 }}>Adicionar músicas</div>
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar música…" data-testid="setlist-song-search" style={{ marginBottom: 8, height: 44 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 280, overflowY: "auto" }}>
                {available.map((s) => (
                  <button key={s.id} onClick={() => setIds([...ids, String(s.id)])} className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, cursor: "pointer", color: "inherit", textAlign: "left" }} data-testid={`setlist-add-${s.id}`}>
                    <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 34, height: 34, borderRadius: "var(--r-sm)" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "var(--f-medium)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title}</div>
                      <div className="t-hint">{s.artist}</div>
                    </div>
                    <Icon name="add" size={16} color="var(--brand-primary)" />
                  </button>
                ))}
                {!available.length ? <div className="t-hint">Sem músicas disponíveis.</div> : null}
              </div>
            </div>
          </div>
        </div>

        {members?.length ? (
          <div>
            <div className="t-section" style={{ marginBottom: 8 }}>Cantores por defeito ({singers.length})</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {members.map((m) => (
                <button key={m.id} className={`chip ${singers.includes(String(m.id)) ? "active" : ""}`} onClick={() => toggleSinger(String(m.id))} data-testid={`setlist-singer-${m.id}`}>
                  {m.name}{m.voice ? ` · ${m.voice[0]}` : ""}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {error ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }}>{error}</div> : null}
        <Button loading={save.isPending} onClick={() => { setError(""); if (!name.trim()) return setError("Indique um nome para o alinhamento."); save.mutate(); }} data-testid="setlist-save">
          {existing?.id ? "GUARDAR" : "CRIAR ALINHAMENTO"}
        </Button>
      </div>
    </Sheet>
  );
}

export default function AdminSetlists() {
  const { user: me } = useAuth();
  const canDelete = can(me, "setlists.delete");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | 'new' | setlist(full)
  const [loadingId, setLoadingId] = useState(null);
  const [sp, setSp] = useSearchParams();
  const feature = useFeatureToggle("setlists");

  const q = useQuery({ queryKey: ["admin-setlists"], queryFn: () => api("/setlists") });
  const songsQ = useQuery({ queryKey: ["songs", "", ""], queryFn: () => api("/songs?q=&status=") });
  const membersQ = useQuery({ queryKey: ["members-directory"], queryFn: () => api("/members/directory"), select: (l) => l.filter((m) => m.is_singer && m.active !== false) });
  const list = q.data || [];

  const del = useMutation({
    mutationFn: (id) => api(`/admin/setlists/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-setlists"] }),
  });

  const openEdit = async (sl) => {
    setLoadingId(sl.id);
    try {
      const full = await api(`/setlists/${sl.id}`);
      setEditing({ ...sl, ...full });
    } catch {
      setEditing(sl);
    } finally {
      setLoadingId(null);
    }
  };

  // Deep links: ?new=1[&songs=id,id] (e.g. from Repertório) or ?edit=<setlistId>.
  useEffect(() => {
    if (sp.get("new")) { setEditing({ song_ids: (sp.get("songs") || "").split(",").filter(Boolean), isNew: true }); setSp({}, { replace: true }); }
    else if (sp.get("edit") && q.data) { const sl = q.data.find((x) => x.id === sp.get("edit")); if (sl) openEdit(sl); setSp({}, { replace: true }); }
  }, [sp, q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSaved = () => {
    qc.invalidateQueries({ queryKey: ["admin-setlists"] });
    qc.invalidateQueries({ queryKey: ["setlist"] });
    qc.invalidateQueries({ queryKey: ["home-setlists"] });
    setEditing(null);
  };

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Alinhamentos</h1>
        <Button className="auto sm" icon="add" onClick={() => setEditing("new")} data-testid="setlist-new-button">NOVO</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div className="page ulc-fade" style={{ maxWidth: 820 }}>
          {!list.length ? <EmptyState icon="list" text="Ainda não há alinhamentos. Crie o primeiro." /> : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {list.map((e) => (
              <div key={e.id} className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 14 }} data-testid={`admin-setlist-${e.id}`}>
                <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--brand-tertiary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name="list" size={18} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--f-medium)", fontSize: 15 }}>{e.name || e.title}</div>
                  <div className="t-hint">{(e.songs?.length ?? e.song_ids?.length ?? 0)} músicas{e.description ? ` · ${e.description}` : ""}</div>
                </div>
                <FeatureStar kind="setlists" item={e} toggle={feature} />
                <button className="icon-btn" onClick={() => navigate(`/alinhamentos/${e.id}`)} data-testid={`setlist-open-${e.id}`} title="Abrir como lista de reprodução" aria-label="Abrir alinhamento"><Icon name="play" size={16} /></button>
                <button className="icon-btn" onClick={() => navigate(`/admin/events?setlist=${e.id}`)} data-testid={`setlist-schedule-${e.id}`} title="Agendar numa data"><Icon name="calendar-check" size={16} color="var(--brand-primary)" /></button>
                <button className="icon-btn" onClick={() => openEdit(e)} data-testid={`setlist-edit-${e.id}`}>{loadingId === e.id ? <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> : <Icon name="pencil" size={16} />}</button>
                {canDelete ? <button className="icon-btn" onClick={() => window.confirm(`Apagar "${e.name || e.title}"?`) && del.mutate(e.id)} data-testid={`setlist-delete-${e.id}`}><Icon name="trash" size={16} color="var(--muted)" /></button> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {editing ? (
        <Editor
          existing={editing === "new" ? null : editing}
          allSongs={songsQ.data || []}
          members={membersQ.data || []}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : null}
    </>
  );
}
